class CartDrawerCarousel {
  constructor() {
    this.cartDrawer = document.querySelector('cart-drawer');
    if (!this.cartDrawer) return;

    this.cartDrawer.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    this.cartDrawer.addEventListener('keydown', this.onKeydown.bind(this));

    this.onPortaledMenuClick = this.onPortaledMenuClick.bind(this);

    const observer = new MutationObserver(() => {
      if (!this.cartDrawer.classList.contains('active')) {
        this.closeAllDropdowns();
      }
    });
    observer.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  onClick(event) {
    const trigger = event.target.closest('.cart-drawer-dropdown__trigger');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleDropdown(trigger);
    }
  }

  onPortaledMenuClick(event) {
    const option = event.target.closest('.cart-drawer-dropdown__option');
    if (!option) return;

    event.preventDefault();
    event.stopPropagation();
    this.selectOption(option);
  }

  onDocumentClick(event) {
    if (
      event.target.closest('.cart-drawer-dropdown__trigger') ||
      event.target.closest('.cart-drawer-dropdown__menu--portaled')
    ) {
      return;
    }

    if (!event.target.closest('.cart-drawer-dropdown')) {
      this.closeAllDropdowns();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAllDropdowns();
    }
  }

  toggleDropdown(trigger) {
    const dropdown = trigger.closest('.cart-drawer-dropdown');
    const isOpen = dropdown.classList.contains('is-open');

    this.closeAllDropdowns();

    if (!isOpen) {
      dropdown.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.portalMenu(dropdown);
      requestAnimationFrame(() => this.positionMenu(dropdown));
    }
  }

  getMenu(dropdown) {
    return dropdown.__cartDrawerMenu || dropdown.querySelector('.cart-drawer-dropdown__menu');
  }

  portalMenu(dropdown) {
    const menu = dropdown.querySelector('.cart-drawer-dropdown__menu');
    if (!menu || menu.parentElement === document.body) return;

    dropdown.__cartDrawerMenu = menu;
    menu.__cartDrawerDropdown = dropdown;
    menu.classList.add('cart-drawer-dropdown__menu--portaled', 'is-open');
    menu.addEventListener('click', this.onPortaledMenuClick);
    document.body.appendChild(menu);
  }

  restoreMenu(dropdown) {
    const menu = this.getMenu(dropdown);
    if (!menu || menu.parentElement === dropdown) return;

    menu.removeEventListener('click', this.onPortaledMenuClick);
    menu.classList.remove('cart-drawer-dropdown__menu--portaled', 'is-open');
    dropdown.appendChild(menu);
    delete menu.__cartDrawerDropdown;
    delete dropdown.__cartDrawerMenu;
  }

  getStickyCheckoutHeight() {
    const stickyCheckout = this.cartDrawer.querySelector('.cart-drawer__sticky-checkout');
    if (!stickyCheckout || !this.cartDrawer.classList.contains('active')) return 0;

    return stickyCheckout.getBoundingClientRect().height;
  }

  positionMenu(dropdown) {
    const trigger = dropdown.querySelector('.cart-drawer-dropdown__trigger');
    const menu = this.getMenu(dropdown);
    if (!trigger || !menu) return;

    const triggerRect = trigger.getBoundingClientRect();
    const gap = 4;
    const stickyCheckoutHeight = this.getStickyCheckoutHeight();
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap - stickyCheckoutHeight;
    const minVisibleHeight = 96;
    const maxHeight = Math.max(minVisibleHeight, Math.min(224, Math.max(spaceBelow - 8, minVisibleHeight)));

    menu.style.position = 'fixed';
    menu.style.left = `${triggerRect.left}px`;
    menu.style.width = `${triggerRect.width}px`;
    menu.style.right = 'auto';
    menu.style.top = `${triggerRect.bottom + gap}px`;
    menu.style.zIndex = '1100';
    menu.style.maxHeight = `${maxHeight}px`;
  }

  resetMenuPosition(dropdown) {
    const menu = this.getMenu(dropdown);
    if (!menu) return;

    menu.style.position = '';
    menu.style.left = '';
    menu.style.top = '';
    menu.style.right = '';
    menu.style.width = '';
    menu.style.maxHeight = '';
    menu.style.zIndex = '';
    this.restoreMenu(dropdown);
  }

  closeAllDropdowns() {
    this.cartDrawer.querySelectorAll('.cart-drawer-dropdown.is-open').forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      dropdown.querySelector('.cart-drawer-dropdown__trigger')?.setAttribute('aria-expanded', 'false');
      this.resetMenuPosition(dropdown);
    });
  }

  selectOption(option) {
    if (option.disabled) return;

    const menu = option.closest('.cart-drawer-dropdown__menu');
    const dropdown =
      menu?.__cartDrawerDropdown || option.closest('.cart-drawer-dropdown');
    const card = dropdown?.closest('.cart-drawer-product-card');
    const form = dropdown?.closest('form') || card?.querySelector('form');
    const trigger = dropdown?.querySelector('.cart-drawer-dropdown__trigger');
    const label = trigger?.querySelector('.cart-drawer-dropdown__label');
    const optionContainer = menu || dropdown;

    if (!dropdown || !card || !form || !trigger || !label || !optionContainer) return;

    optionContainer.querySelectorAll('.cart-drawer-dropdown__option').forEach((item) => {
      item.classList.remove('is-selected');
      item.setAttribute('aria-selected', 'false');
    });

    option.classList.add('is-selected');
    option.setAttribute('aria-selected', 'true');
    label.textContent = option.dataset.label || option.textContent.trim();

    this.applyVariantSelection(card, form, option);
    this.closeAllDropdowns();
  }

  applyVariantSelection(card, form, option) {
    const variantInput = form.querySelector('[name="id"]');
    const submitButton = form.querySelector('[type="submit"]');
    const submitLabel = submitButton?.querySelector('span');
    const priceElement = card.querySelector(
      '[data-cart-drawer-price] .price-item--regular, [data-cart-drawer-price] .price-item--sale'
    );

    if (!variantInput || !submitButton || !submitLabel) return;

    variantInput.value = option.dataset.value;
    const isAvailable = option.dataset.available === 'true';
    variantInput.disabled = !isAvailable;
    submitButton.disabled = !isAvailable;
    submitLabel.textContent = isAvailable ? 'Add to Bag' : window.variantStrings?.soldOut || 'Sold out';

    if (priceElement && option.dataset.price) {
      priceElement.textContent = option.dataset.price;
    }
  }
}

new CartDrawerCarousel();
